/**
 * Context graph service API client — graph nodes, edges, subgraphs.
 *
 * Requests are routed through the gateway (/api/v1/graph/*) which
 * proxies to context_graph_service.
 */

import { config } from '@/config/environment';
import { getAuthToken } from '@/contexts/AuthContext';
import type { GraphOverviewResponse, SubgraphResponse } from '@/types/api';
import axios from 'axios';

const graphApi = axios.create({
  baseURL: config.GATEWAY_URL,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

graphApi.interceptors.request.use((reqConfig) => {
  const token = getAuthToken();
  if (token) {
    reqConfig.headers.Authorization = `Bearer ${token}`;
  }
  return reqConfig;
});

/** Fetch the most-connected entities with their edges for the initial view. */
export async function fetchGraphOverview(
  limit: number = 100,
  asOf?: string,
): Promise<GraphOverviewResponse> {
  const params: Record<string, unknown> = { limit };
  if (asOf) params.as_of = asOf;
  const { data } = await graphApi.get<GraphOverviewResponse>(
    '/api/v1/graph/entities/overview',
    { params },
  );
  return data;
}

/**
 * Fetch the subgraph centered on a canonical entity. `topKPerType` keeps at most
 * N edges of each relationship type per level (the server applies 50 past depth 1
 * when omitted).
 */
export async function fetchEntitySubgraph(
  entityId: string,
  depth: number = 1,
  asOf?: string,
  topKPerType?: number,
): Promise<SubgraphResponse> {
  const params: Record<string, unknown> = { depth };
  if (asOf) params.as_of = asOf;
  if (topKPerType) params.top_k_per_type = topKPerType;
  const { data } = await graphApi.get<SubgraphResponse>(
    `/api/v1/graph/entities/${entityId}/subgraph`,
    { params },
  );
  return data;
}
